/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/workforce/Dashboard.tsx
 * ----------------------------------------------------------------------------
 * Dashboard de indicadores do Workforce (D16-3b). Consome GET /workforce/dashboard/.
 * - Forma UNICA: itera payload.empresas (1 elem = a sua empresa; N = visao de grupo).
 * - "Agora" (estado vivo): fila por prioridade + em execucao + idade max da fila.
 * - "Periodo": aceite/execucao medios + SLA breaches/exec overruns + BarChart por tipo.
 * - Seletor de dias (7/14/30). Atualizacao manual (botao). Estilo claro/utilitario
 *   alinhado a pages/workforce/Skills.tsx (NAO o tema escuro do demo).
 * ========================================================================== */

import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
} from "recharts";
import { getWorkforceDashboard, type EmpresaKPIs } from "@/api/workforce";

function cls(...xs: Array<string | false | null | undefined>) {
    return xs.filter(Boolean).join(" ");
}

/** Segundos -> humano: null/"—"; <60 -> "Ns"; senao "Mm Ns". */
function fmtDur(s: number | null | undefined): string {
    if (s == null) return "—";
    const sec = Math.round(s);
    if (sec < 60) return `${sec}s`;
    return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

const DAYS_OPTIONS = [7, 14, 30];

export default function WorkforceDashboard() {
    const [days, setDays] = useState(7);

    const q = useQuery({
        queryKey: ["wf-dashboard", days],
        queryFn: () => getWorkforceDashboard(days),
        staleTime: 30_000,
    });

    const payload = q.data;

    return (
        <div className="p-4">
            {/* Header */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h2 className="text-lg font-semibold">Workforce • Dashboard de indicadores</h2>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        Fila, execucao e tempos medios por empresa. "Agora" (estado vivo) + "Periodo".
                    </p>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex overflow-hidden rounded-md border border-neutral-300 dark:border-neutral-700">
                        {DAYS_OPTIONS.map((d) => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={cls(
                                    "h-9 px-3 text-sm",
                                    days === d
                                        ? "bg-blue-600 text-white"
                                        : "bg-transparent text-neutral-700 dark:text-neutral-300"
                                )}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => q.refetch()}
                        className="h-9 rounded-md border border-neutral-300 px-3 text-sm dark:border-neutral-700"
                    >
                        Atualizar
                    </button>
                </div>
            </div>

            {/* Estados */}
            {q.isLoading && (
                <div className="rounded-md border border-neutral-200 p-6 dark:border-neutral-800">
                    Carregando indicadores…
                </div>
            )}

            {q.isError && (
                <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
                    {(q.error as Error)?.message || "Erro ao carregar o dashboard."}
                </div>
            )}

            {/* Conteudo */}
            {!q.isLoading && !q.isError && payload && (
                <>
                    {/* Banner do scope */}
                    <div className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900/40 dark:text-neutral-400">
                        {payload.scope === "group"
                            ? `Visao de grupo — ${payload.empresas.length} empresa(s) • ultimos ${payload.days} dias`
                            : `A ver: a sua empresa • ultimos ${payload.days} dias`}
                    </div>

                    {payload.empresas.length === 0 && (
                        <div className="rounded-md border border-neutral-200 p-6 text-center text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                            Nenhuma empresa para mostrar.
                        </div>
                    )}

                    {/* Seccoes empilhadas (1 ou N empresas) */}
                    <div className="space-y-5">
                        {payload.empresas.map((emp) => (
                            <EmpresaBlock key={emp.company_id} emp={emp} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// -------------------------- Bloco por empresa --------------------------------
function EmpresaBlock({ emp }: { emp: EmpresaKPIs }) {
    const { agora, periodo } = emp;
    const fila = agora.fila_por_prioridade;

    const chartData = Object.entries(periodo.execucao_media_por_tipo_s).map(([tipo, seg]) => ({
        tipo,
        segundos: seg == null ? 0 : Math.round(seg),
    }));

    return (
        <section className="overflow-hidden rounded-md border border-neutral-200 dark:border-neutral-800">
            <header className="border-b border-neutral-200 bg-neutral-50 px-4 py-2 dark:border-neutral-800 dark:bg-neutral-900">
                <h3 className="text-base font-semibold">{emp.company_name}</h3>
            </header>

            <div className="space-y-5 p-4">
                {/* AGORA */}
                <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Agora
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <KpiCard
                            label="Fila total"
                            value={agora.fila_total}
                            sub={`alta ${fila.alta} • media ${fila.media} • baixa ${fila.baixa}`}
                        />
                        <KpiCard label="Em execucao" value={agora.em_execucao} />
                        <KpiCard label="Idade max. da fila" value={fmtDur(agora.fila_idade_max_s)} />
                    </div>
                </div>

                {/* PERIODO */}
                <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        Periodo
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <KpiCard label="Aceite medio" value={fmtDur(periodo.aceite_medio_s)} />
                        <KpiCard label="Execucao media" value={fmtDur(periodo.execucao_media_s)} />
                        <KpiCard label="SLA breaches" value={periodo.sla_breaches} />
                        <KpiCard label="Exec overruns" value={periodo.exec_overruns} />
                    </div>

                    <div className="mt-4 rounded-md border border-neutral-200 p-4 dark:border-neutral-800">
                        <div className="mb-3 text-sm font-medium">Execucao media por tipo</div>
                        {chartData.length === 0 ? (
                            <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                Sem dados no periodo.
                            </div>
                        ) : (
                            <div style={{ width: "100%", height: 240 }}>
                                <ResponsiveContainer>
                                    <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            stroke="rgba(100,116,139,0.2)"
                                            vertical={false}
                                        />
                                        <XAxis
                                            dataKey="tipo"
                                            fontSize={11}
                                            tickLine={false}
                                            axisLine={false}
                                        />
                                        <YAxis
                                            fontSize={11}
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(v: number) => fmtDur(v)}
                                            width={64}
                                        />
                                        <Tooltip formatter={(v) => fmtDur(Number(v))} />
                                        <Bar dataKey="segundos" fill="#2563eb" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

// -------------------------- KPI card (claro) ---------------------------------
function KpiCard({
    label,
    value,
    sub,
}: {
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
}) {
    return (
        <div className="rounded-md border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
            <div className="text-[11px] font-medium uppercase tracking-wide text-neutral-500">{label}</div>
            <div className="mt-1 text-2xl font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
                {value}
            </div>
            {sub && <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">{sub}</div>}
        </div>
    );
}
